/**
 * Forget the HubSpot chat visitor on logout.
 *
 * The public order pages load the HubSpot chat anonymously. HubSpot keeps the
 * visitor (and their chat thread) in first-party cookies, not in memory, so a
 * full-page logout alone would let the next person on the same browser open
 * the order-page chat inside the previous user's thread / contact. Logout
 * therefore asks the widget to clear itself and expires HubSpot's visitor
 * cookies on this host and its parent domains (HubSpot sets them on the
 * registrable domain, e.g. .limephone.io).
 *
 * The server's GET /logout page runs the same cookie list (HUBSPOT_VISITOR_COOKIES),
 * which covers the mobile app's logout, which navigates there directly.
 */

/** HubSpot's chat + visitor cookies. */
export const HUBSPOT_VISITOR_COOKIES = ['messagesUtk', 'hubspotutk', '__hstc', '__hssc', '__hssrc'] as const;

/** The host and each parent domain a cookie could be set on (never a bare TLD). */
export function cookieDomainsFor(hostname: string): string[] {
  const host = (hostname || '').toLowerCase();
  if (!host || /^[\d.]+$/.test(host) || !host.includes('.')) return [host].filter(Boolean);
  const parts = host.split('.');
  const out = [host];
  for (let i = 1; i < parts.length - 1; i++) out.push('.' + parts.slice(i).join('.'));
  return out;
}

/** document.cookie assignments that expire the HubSpot visitor cookies. */
export function hubspotCookieExpiries(hostname: string): string[] {
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  const out: string[] = [];
  for (const name of HUBSPOT_VISITOR_COOKIES) {
    out.push(`${name}=; ${expired}`);
    for (const d of cookieDomainsFor(hostname)) out.push(`${name}=; ${expired}; domain=${d}`);
  }
  return out;
}

/** Best effort: never let this block a logout. */
export function clearHubspotChatIdentity(win: Window = window): void {
  try {
    const s = win.hsConversationsSettings as Record<string, unknown> | undefined;
    if (s) {
      delete s.identificationEmail;
      delete s.identificationToken;
    }
    win.HubSpotConversations?.clear?.({ resetWidget: true });
  } catch { /* the widget may not be loaded */ }
  try {
    for (const c of hubspotCookieExpiries(win.location.hostname)) win.document.cookie = c;
  } catch { /* cookies blocked */ }
}
